import { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from './button';
import api from '../../lib/api';
import { toast } from 'sonner';

export const ImageUpload = ({ value, onChange, label = "Immagine", description, acceptIco = false }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - include ico and svg if acceptIco is true
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (acceptIco) {
      validTypes.push('image/x-icon', 'image/vnd.microsoft.icon', 'image/svg+xml');
    }
    
    // Also check by extension for .ico files (some browsers report different MIME types)
    const extension = file.name.split('.').pop()?.toLowerCase();
    const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    if (acceptIco) {
      validExtensions.push('ico', 'svg');
    }
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(extension)) {
      const formats = acceptIco ? 'JPG, PNG, GIF, WebP, ICO o SVG' : 'JPG, PNG, GIF o WebP';
      toast.error(`Tipo file non valido. Usa: ${formats}`);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File troppo grande. Max 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onChange(res.data.url);
      toast.success('Immagine caricata');
    } catch (error) {
      toast.error('Errore nel caricamento');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const acceptTypes = acceptIco 
    ? "image/jpeg,image/png,image/gif,image/webp,image/x-icon,image/vnd.microsoft.icon,image/svg+xml,.ico,.svg"
    : "image/jpeg,image/png,image/gif,image/webp";

  const formatText = acceptIco ? 'JPG, PNG, GIF, WebP, ICO o SVG' : 'JPG, PNG, GIF o WebP';

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
      
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptTypes}
        onChange={handleUpload}
        className="hidden"
        data-testid="image-upload-input"
      />

      {value ? (
        <div className="relative group">
          <img
            src={getFullUrl(value)}
            alt="Preview"
            className="w-full h-48 object-cover rounded-lg border border-white/10"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Upload size={16} className="mr-2" />
              Cambia
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleRemove}
            >
              <X size={16} />
            </Button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="w-full h-48 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-primary/50 hover:bg-white/5 transition-all"
        >
          {uploading ? (
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
          ) : (
            <>
              <ImageIcon size={32} className="text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Clicca per caricare un'immagine
              </p>
              <p className="text-xs text-muted-foreground">
                {formatText} (max 5MB)
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};
